import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { 
  Mail, Send, Edit, Loader2, CheckCircle2, XCircle, Settings,
  User, Users, Bell, FileText, Image, Save, Eye, TestTube, Code
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/axios';
import { cn } from '../../lib/utils';


const EmailSettingsPage = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('general');
  
  // Notification settings
  const [notificationSettings, setNotificationSettings] = useState([]);
  
  // Email templates
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [templateSubject, setTemplateSubject] = useState('');
  const [templateContent, setTemplateContent] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);
  
  // Signature settings
  const [signatureSettings, setSignatureSettings] = useState({
    use_account_manager: true,
    default_signature_name: '',
    default_signature_email: '',
    default_signature_phone: '',
    logo_url: '',
    banner_url: '',
    footer_text: ''
  });
  const [savingSignature, setSavingSignature] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  
  // Test email
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  
  // Global email settings (BCC, etc.)
  const [globalEmailSettings, setGlobalEmailSettings] = useState({
    bcc_enabled: false,
    bcc_email: '',
    bcc_on_all_emails: true
  });
  const [savingGlobal, setSavingGlobal] = useState(false);

  // Handle image upload
  const handleImageUpload = async (e, imageType) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file size
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Le fichier est trop volumineux. Maximum 2MB.');
      return;
    }
    
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('image_type', imageType);
      
      const res = await axios.post(`${API}/email/settings/signature/upload-image`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      const fieldName = imageType === 'logo' ? 'logo_url' : 'banner_url';
      setSignatureSettings(prev => ({ ...prev, [fieldName]: res.data.url }));
      toast.success(`${imageType === 'logo' ? 'Logo' : 'Bannière'} téléversé avec succès`);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors du téléversement');
    } finally {
      setUploadingImage(false);
      e.target.value = ''; // Reset input
    }
  };

  // Handle image deletion
  const handleDeleteImage = async (imageType) => {
    try {
      await api.delete('/email/settings/signature/delete-image?image_type=${imageType}');
      
      const fieldName = imageType === 'logo' ? 'logo_url' : 'banner_url';
      setSignatureSettings(prev => ({ ...prev, [fieldName]: null }));
      toast.success(`${imageType === 'logo' ? 'Logo' : 'Bannière'} supprimé`);
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [notifRes, templatesRes, sigRes, globalRes] = await Promise.all([
        api.get('/email/settings/notifications'),
        api.get('/email/templates/'),
        api.get('/email/settings/signature'),
        api.get('/email/settings/global').catch(() => ({ data: {} }))
      ]);
      
      setNotificationSettings(notifRes.data || []);
      setTemplates(templatesRes.data || []);
      setSignatureSettings(sigRes.data || {});
      setGlobalEmailSettings(globalRes.data || { bcc_enabled: false, bcc_email: '', bcc_on_all_emails: true });
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement des paramètres');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleNotification = async (eventType, field, value) => {
    try {
      const setting = notificationSettings.find(s => s.event_type === eventType) || {};
      const updatedSetting = {
        ...setting,
        event_type: eventType,
        [field]: value
      };
      
      await axios.put(`${API}/email/settings/notifications/${eventType}`, updatedSetting, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setNotificationSettings(prev => 
        prev.map(s => s.event_type === eventType ? { ...s, [field]: value } : s)
      );
      
      toast.success('Paramètre mis à jour');
    } catch (error) {
      console.error('Error updating notification:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const openTemplateEditor = (template) => {
    setSelectedTemplate(template);
    setTemplateSubject(template.subject || '');
    setTemplateContent(template.html_content || '');
    setShowTemplateModal(true);
  };

  const handleSaveTemplate = async () => {
    if (!selectedTemplate) return;
    
    setSavingTemplate(true);
    try {
      await axios.post(`${API}/email/templates/`, {
        name: selectedTemplate.name,
        subject: templateSubject,
        html_content: templateContent,
        description: selectedTemplate.description,
        active: true,
        variables: selectedTemplate.variables || []
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Template sauvegardé');
      setShowTemplateModal(false);
      fetchData();
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleSaveSignature = async () => {
    setSavingSignature(true);
    try {
      await axios.put(`${API}/email/settings/signature`, signatureSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Paramètres de signature sauvegardés');
    } catch (error) {
      console.error('Error saving signature:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSavingSignature(false);
    }
  };

  const handleSaveGlobalSettings = async () => {
    setSavingGlobal(true);
    try {
      await axios.put(`${API}/email/settings/global`, globalEmailSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Paramètres globaux sauvegardés');
    } catch (error) {
      console.error('Error saving global settings:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSavingGlobal(false);
    }
  };

  const handleSendTestEmail = async (templateName) => {
    if (!testEmail) {
      toast.error('Veuillez entrer une adresse email');
      return;
    }
    
    setSendingTest(true);
    try {
      await axios.post(`${API}/email/send-test`, {
        template_name: templateName,
        recipient_email: testEmail
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Email de test envoyé !');
    } catch (error) {
      console.error('Error sending test:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSendingTest(false);
    }
  };

  const getEventIcon = (eventType) => {
    if (eventType.includes('ticket')) return <Mail className="w-4 h-4" />;
    if (eventType.includes('task')) return <CheckCircle2 className="w-4 h-4" />;
    if (eventType.includes('project')) return <FileText className="w-4 h-4" />;
    if (eventType.includes('invoice') || eventType.includes('payment')) return <FileText className="w-4 h-4" />;
    if (eventType.includes('domain')) return <Bell className="w-4 h-4" />;
    return <Bell className="w-4 h-4" />;
  };

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
      
      <div className={cn("transition-all duration-300", collapsed ? "md:ml-20" : "md:ml-64")}>
        <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
        
        <main className="p-4 md:p-8 pt-32">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold" style={{ color: theme.colors?.primary }}>
                Notifications Email
              </h1>
              <p className="text-slate-500 mt-1">Configurez les emails automatiques et les templates</p>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : (
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="mb-6">
                <TabsTrigger value="general">
                  <Settings className="w-4 h-4 mr-2" />
                  Général
                </TabsTrigger>
                <TabsTrigger value="notifications">
                  <Bell className="w-4 h-4 mr-2" />
                  Notifications
                </TabsTrigger>
                <TabsTrigger value="templates">
                  <FileText className="w-4 h-4 mr-2" />
                  Templates
                </TabsTrigger>
                <TabsTrigger value="signature">
                  <Edit className="w-4 h-4 mr-2" />
                  Signature
                </TabsTrigger>
              </TabsList>

              {/* General Tab - BCC Settings */}
              <TabsContent value="general">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Mail className="w-5 h-5" />
                      Copie cachée (BCC)
                    </CardTitle>
                    <CardDescription>
                      Recevez une copie de tous les emails envoyés aux clients
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                      <div>
                        <Label className="text-base font-medium">Activer la copie cachée</Label>
                        <p className="text-sm text-slate-500 mt-1">
                          Tous les emails sortants seront copiés à l&apos;adresse configurée
                        </p>
                      </div>
                      <Switch
                        checked={globalEmailSettings.bcc_enabled}
                        onCheckedChange={(v) => setGlobalEmailSettings(prev => ({ ...prev, bcc_enabled: v }))}
                      />
                    </div>
                    
                    {globalEmailSettings.bcc_enabled && (
                      <div className="space-y-4 pl-4 border-l-2 border-cyan-200">
                        <div className="space-y-2">
                          <Label htmlFor="bcc_email">Adresse email BCC</Label>
                          <Input
                            id="bcc_email"
                            type="email"
                            placeholder="copie@agence.fr"
                            value={globalEmailSettings.bcc_email}
                            onChange={(e) => setGlobalEmailSettings(prev => ({ ...prev, bcc_email: e.target.value }))}
                          />
                          <p className="text-xs text-slate-500">
                            Cette adresse recevra une copie cachée de tous les emails envoyés
                          </p>
                        </div>
                        
                        <div className="flex items-center gap-3">
                          <Switch
                            id="bcc_all"
                            checked={globalEmailSettings.bcc_on_all_emails}
                            onCheckedChange={(v) => setGlobalEmailSettings(prev => ({ ...prev, bcc_on_all_emails: v }))}
                          />
                          <Label htmlFor="bcc_all" className="cursor-pointer">
                            Appliquer à tous les emails (notifications auto + manuels)
                          </Label>
                        </div>
                      </div>
                    )}
                    
                    <div className="flex justify-end pt-4 border-t">
                      <Button 
                        onClick={handleSaveGlobalSettings}
                        disabled={savingGlobal}
                        style={{ backgroundColor: theme.colors?.accent }}
                      >
                        {savingGlobal ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Save className="w-4 h-4 mr-2" />
                        )}
                        Enregistrer
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Notifications Tab */}
              <TabsContent value="notifications">
                <Card>
                  <CardHeader>
                    <CardTitle>Paramètres des notifications</CardTitle>
                    <CardDescription>
                      Activez ou désactivez les emails pour chaque type d'événement
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Événement</TableHead>
                          <TableHead className="text-center">Activé</TableHead>
                          <TableHead className="text-center">Client</TableHead>
                          <TableHead className="text-center">Agence</TableHead>
                          <TableHead className="text-center">Assigné</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {notificationSettings.map((setting) => (
                          <TableRow key={setting.event_type}>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                {getEventIcon(setting.event_type)}
                                <span className="font-medium">{setting.event_label}</span>
                              </div>
                            </TableCell>
                            <TableCell className="text-center">
                              <Switch 
                                checked={setting.enabled}
                                onCheckedChange={(v) => handleToggleNotification(setting.event_type, 'enabled', v)}
                              />
                            </TableCell>
                            <TableCell className="text-center">
                              <Switch 
                                checked={setting.send_to_client}
                                onCheckedChange={(v) => handleToggleNotification(setting.event_type, 'send_to_client', v)}
                                disabled={!setting.enabled}
                              />
                            </TableCell>
                            <TableCell className="text-center">
                              <Switch 
                                checked={setting.send_to_agency}
                                onCheckedChange={(v) => handleToggleNotification(setting.event_type, 'send_to_agency', v)}
                                disabled={!setting.enabled}
                              />
                            </TableCell>
                            <TableCell className="text-center">
                              <Switch 
                                checked={setting.send_to_assignee}
                                onCheckedChange={(v) => handleToggleNotification(setting.event_type, 'send_to_assignee', v)}
                                disabled={!setting.enabled}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Templates Tab */}
              <TabsContent value="templates">
                <div className="space-y-4">
                  {/* Test Email Input */}
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-4">
                        <TestTube className="w-5 h-5 text-slate-400" />
                        <Input 
                          placeholder="Email de test (ex: votre@email.com)"
                          value={testEmail}
                          onChange={(e) => setTestEmail(e.target.value)}
                          className="max-w-xs"
                        />
                        <span className="text-sm text-slate-500">
                          Entrez votre email pour tester les templates
                        </span>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Templates List */}
                  <div className="grid gap-4">
                    {templates.map((template) => (
                      <Card key={template.name}>
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <Mail className="w-4 h-4 text-slate-400" />
                                <h3 className="font-semibold">{template.name}</h3>
                                {template.html_content ? (
                                  <Badge className="bg-emerald-100 text-emerald-700 text-xs">Personnalisé</Badge>
                                ) : (
                                  <Badge variant="outline" className="text-xs">Par défaut</Badge>
                                )}
                              </div>
                              <p className="text-sm text-slate-500 mb-2">{template.description}</p>
                              <p className="text-sm">
                                <span className="text-slate-400">Sujet: </span>
                                <code className="bg-slate-100 px-1 rounded text-xs">{template.subject}</code>
                              </p>
                              {template.variables?.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-2">
                                  {template.variables.map((v) => (
                                    <Badge key={v} variant="outline" className="text-[10px]">
                                      {`{{${v}}}`}
                                    </Badge>
                                  ))}
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setSelectedTemplate(template)}
                              >
                                <Eye className="w-3 h-3 mr-1" />
                                Aperçu
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleSendTestEmail(template.name)}
                                disabled={sendingTest || !testEmail}
                              >
                                {sendingTest ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                                <span className="ml-1">Test</span>
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => openTemplateEditor(template)}
                                style={{ backgroundColor: theme.colors?.accent }}
                              >
                                <Edit className="w-3 h-3 mr-1" />
                                Modifier
                              </Button>
                            </div>
                          </div>
                          
                          {/* Content Preview */}
                          {selectedTemplate?.name === template.name && (
                            <div className="mt-4 pt-4 border-t">
                              <div className="flex items-center justify-between mb-3">
                                <h4 className="text-sm font-medium text-slate-700">Aperçu du contenu</h4>
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  onClick={() => setSelectedTemplate(null)}
                                >
                                  Masquer
                                </Button>
                              </div>
                              <div className="bg-slate-50 border rounded-lg p-4 max-h-80 overflow-y-auto">
                                {template.html_content ? (
                                  <div 
                                    className="prose prose-sm max-w-none"
                                    dangerouslySetInnerHTML={{ __html: template.html_content }}
                                  />
                                ) : template.default_content ? (
                                  <div 
                                    className="prose prose-sm max-w-none"
                                    dangerouslySetInnerHTML={{ __html: template.default_content }}
                                  />
                                ) : (
                                  <p className="text-slate-400 italic text-sm">
                                    Aucun contenu personnalisé. Le template par défaut du système sera utilisé.
                                  </p>
                                )}
                              </div>
                              <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                                <p className="text-xs text-blue-700">
                                  <strong>Variables disponibles:</strong> Les balises {`{{variable}}`} seront remplacées par les vraies valeurs lors de l'envoi.
                                </p>
                                {template.variables?.length > 0 && (
                                  <div className="mt-2 flex flex-wrap gap-1">
                                    {template.variables.map((v) => (
                                      <code key={v} className="text-xs bg-blue-100 px-1 py-0.5 rounded">
                                        {`{{${v}}}`}
                                      </code>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              </TabsContent>

              {/* Signature Tab */}
              <TabsContent value="signature">
                <Card>
                  <CardHeader>
                    <CardTitle>Signature des emails</CardTitle>
                    <CardDescription>
                      Personnalisez la signature qui apparaît dans tous les emails
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Logo d'entête */}
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <Image className="w-4 h-4" />
                        Logo d'entête des emails
                      </Label>
                      <div className="flex items-center gap-4">
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/gif,image/webp"
                          className="hidden"
                          id="logo-upload"
                          onChange={(e) => handleImageUpload(e, 'logo')}
                        />
                        <Button 
                          variant="outline" 
                          onClick={() => document.getElementById('logo-upload').click()}
                          disabled={uploadingImage}
                        >
                          {uploadingImage ? 'Envoi...' : 'Choisir un logo'}
                        </Button>
                        {signatureSettings.logo_url && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-red-600"
                            onClick={() => handleDeleteImage('logo')}
                          >
                            Supprimer
                          </Button>
                        )}
                      </div>
                      {signatureSettings.logo_url && (
                        <div className="p-4 bg-slate-100 rounded-lg">
                          <img 
                            src={signatureSettings.logo_url} 
                            alt="Logo preview" 
                            className="h-16 object-contain"
                            onError={(e) => e.target.style.display = 'none'}
                          />
                        </div>
                      )}
                      <p className="text-xs text-slate-500">Format: PNG, JPG, GIF, WebP. Max 2MB. Taille recommandée: 200x60px</p>
                    </div>

                    {/* Bannière de signature */}
                    <div className="space-y-2 border-t pt-4">
                      <Label className="flex items-center gap-2">
                        <Image className="w-4 h-4" />
                        Bannière de signature (en bas des emails)
                      </Label>
                      <div className="flex items-center gap-4">
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/gif,image/webp"
                          className="hidden"
                          id="banner-upload"
                          onChange={(e) => handleImageUpload(e, 'banner')}
                        />
                        <Button 
                          variant="outline" 
                          onClick={() => document.getElementById('banner-upload').click()}
                          disabled={uploadingImage}
                        >
                          {uploadingImage ? 'Envoi...' : 'Choisir une bannière'}
                        </Button>
                        {signatureSettings.banner_url && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-red-600"
                            onClick={() => handleDeleteImage('banner')}
                          >
                            Supprimer
                          </Button>
                        )}
                      </div>
                      {signatureSettings.banner_url && (
                        <div className="p-4 bg-slate-100 rounded-lg">
                          <img 
                            src={signatureSettings.banner_url} 
                            alt="Banner preview" 
                            className="max-h-32 object-contain"
                            onError={(e) => e.target.style.display = 'none'}
                          />
                        </div>
                      )}
                      <p className="text-xs text-slate-500">Format: PNG, JPG, GIF, WebP. Max 2MB. Taille recommandée: 600x150px</p>
                    </div>

                    {/* Account Manager Option */}
                    <div className="flex items-center justify-between border-t pt-4">
                      <div>
                        <Label>Utiliser les coordonnées du responsable de compte</Label>
                        <p className="text-sm text-slate-500">
                          Si désactivé, les coordonnées par défaut ci-dessous seront utilisées
                        </p>
                      </div>
                      <Switch 
                        checked={signatureSettings.use_account_manager}
                        onCheckedChange={(v) => setSignatureSettings({...signatureSettings, use_account_manager: v})}
                      />
                    </div>

                    {/* Default Signature */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 border-t pt-4">
                      <div className="space-y-2">
                        <Label>Nom par défaut</Label>
                        <Input 
                          value={signatureSettings.default_signature_name || ''}
                          onChange={(e) => setSignatureSettings({...signatureSettings, default_signature_name: e.target.value})}
                          placeholder="L'équipe MSC"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Email par défaut</Label>
                        <Input 
                          value={signatureSettings.default_signature_email || ''}
                          onChange={(e) => setSignatureSettings({...signatureSettings, default_signature_email: e.target.value})}
                          placeholder="contact@msc.fr"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Téléphone par défaut</Label>
                        <Input 
                          value={signatureSettings.default_signature_phone || ''}
                          onChange={(e) => setSignatureSettings({...signatureSettings, default_signature_phone: e.target.value})}
                          placeholder="01 23 45 67 89"
                        />
                      </div>
                    </div>

                    {/* Footer Text */}
                    <div className="space-y-2 border-t pt-4">
                      <Label>Texte de pied de page</Label>
                      <Textarea 
                        value={signatureSettings.footer_text || ''}
                        onChange={(e) => setSignatureSettings({...signatureSettings, footer_text: e.target.value})}
                        placeholder="Cet email a été envoyé automatiquement..."
                        rows={2}
                      />
                    </div>

                    {/* Save Button */}
                    <div className="flex justify-end border-t pt-4">
                      <Button 
                        onClick={handleSaveSignature} 
                        disabled={savingSignature}
                        style={{ backgroundColor: theme.colors?.accent }}
                      >
                        {savingSignature && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                        <Save className="w-4 h-4 mr-2" />
                        Sauvegarder
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          )}
        </main>
      </div>

      {/* Template Editor Modal */}
      <Dialog open={showTemplateModal} onOpenChange={setShowTemplateModal}>
        <DialogContent className="sm:max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Modifier le template: {selectedTemplate?.name}
            </DialogTitle>
            <DialogDescription>
              {selectedTemplate?.description || 'Personnalisez le sujet et le contenu de cet email'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 py-4">
            {/* Left Column - Editor */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Sujet de l'email
                </Label>
                <Input 
                  value={templateSubject}
                  onChange={(e) => setTemplateSubject(e.target.value)}
                  placeholder="Ex: 🎫 Nouveau ticket créé : {{ticket_subject}}"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Code className="w-4 h-4" />
                  Contenu du message
                </Label>
                <Textarea 
                  value={templateContent}
                  onChange={(e) => setTemplateContent(e.target.value)}
                  rows={18}
                  className="font-mono text-xs"
                  placeholder="<h2>Titre</h2><p>Contenu...</p>"
                />
              </div>
              
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-xs font-medium text-slate-700 mb-2">Variables disponibles :</p>
                <div className="flex flex-wrap gap-1">
                  {selectedTemplate?.variables?.map(v => (
                    <button
                      key={v}
                      onClick={() => {
                        const textarea = document.querySelector('textarea');
                        const start = textarea.selectionStart;
                        const end = textarea.selectionEnd;
                        const newContent = templateContent.substring(0, start) + `{{${v}}}` + templateContent.substring(end);
                        setTemplateContent(newContent);
                      }}
                      className="px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded text-xs hover:bg-indigo-200 transition-colors"
                    >
                      {`{{${v}}}`}
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="p-3 bg-blue-50 rounded-lg text-xs text-blue-700">
                <p className="font-medium mb-1">💡 Conseils :</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Utilisez <code className="bg-blue-100 px-1 rounded">&lt;h2&gt;</code> pour les titres</li>
                  <li>Utilisez <code className="bg-blue-100 px-1 rounded">&lt;p&gt;</code> pour les paragraphes</li>
                  <li>Utilisez <code className="bg-blue-100 px-1 rounded">class="button"</code> pour les boutons</li>
                  <li>Les styles CSS sont automatiquement appliqués</li>
                </ul>
              </div>
            </div>
            
            {/* Right Column - Preview */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Eye className="w-4 h-4" />
                Aperçu de l'email
              </Label>
              <div className="border rounded-lg overflow-hidden bg-slate-100">
                <div className="bg-slate-200 px-3 py-2 flex items-center gap-2 border-b">
                  <div className="w-3 h-3 rounded-full bg-red-400" />
                  <div className="w-3 h-3 rounded-full bg-yellow-400" />
                  <div className="w-3 h-3 rounded-full bg-green-400" />
                  <span className="text-xs text-slate-500 ml-2">Aperçu email</span>
                </div>
                <div 
                  className="bg-white p-4 min-h-[400px] overflow-auto"
                  style={{ maxHeight: '500px' }}
                >
                  {/* Email Preview with wrapper styling */}
                  <div style={{
                    fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, Arial, sans-serif",
                    maxWidth: '100%',
                    margin: '0 auto',
                    background: '#ffffff',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}>
                    {/* Header */}
                    <div style={{
                      background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)',
                      padding: '20px',
                      textAlign: 'center'
                    }}>
                      <div style={{ color: 'white', fontSize: '18px', fontWeight: 'bold' }}>
                        {signatureSettings?.default_signature_name || 'MSC Manager'}
                      </div>
                    </div>
                    
                    {/* Content */}
                    <div 
                      style={{
                        padding: '20px',
                        lineHeight: '1.6',
                        color: '#374151',
                        fontSize: '14px'
                      }}
                      dangerouslySetInnerHTML={{ 
                        __html: templateContent
                          .replace(/\{\{([^}]+)\}\}/g, '<span style="background:#fef3c7;padding:2px 6px;border-radius:4px;color:#92400e;font-family:monospace;font-size:12px;">{{$1}}</span>')
                          .replace(/class="button"/g, 'style="display:inline-block;background:linear-gradient(135deg,#f59e0b,#d97706);color:#fff;padding:12px 24px;text-decoration:none;border-radius:6px;font-weight:600;font-size:13px;"')
                      }}
                    />
                    
                    {/* Footer */}
                    <div style={{
                      background: '#f8fafc',
                      padding: '15px 20px',
                      borderTop: '1px solid #e5e7eb',
                      fontSize: '12px',
                      color: '#64748b'
                    }}>
                      <div style={{ fontWeight: '600', color: '#1e3a5f', marginBottom: '5px' }}>
                        {signatureSettings?.default_signature_name || 'MSC Manager'}
                      </div>
                      <div>
                        📧 {signatureSettings?.default_signature_email || 'contact@agence.fr'}<br />
                        📞 {signatureSettings?.default_signature_phone || '04 84 49 27 00'}<br />
                        {signatureSettings?.footer_text && <span>🌐 {signatureSettings.footer_text}</span>}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter className="flex justify-between items-center">
            <p className="text-xs text-slate-500">
              L'email inclura automatiquement l'en-tête et le pied de page de l'agence
            </p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowTemplateModal(false)}>
                Annuler
              </Button>
              <Button onClick={handleSaveTemplate} disabled={savingTemplate} style={{ backgroundColor: theme.colors?.accent }}>
                {savingTemplate && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Sauvegarder
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmailSettingsPage;
